/**
 * Climb log — pure helpers for the daily climb log (`gyms/{gymId}/climbLogs/{uid}_{YYYY-MM-DD}`).
 *
 * The log is the EVENT stream: every climb (first send or repeat) appends one
 * entry. The `ascents` collection stays the projection that answers the app's
 * hottest question ("did I climb this route?") in O(1) via its deterministic
 * `{uid}_{routeId}` id — see docs/plans/repeat-ascents.md for why both exist.
 *
 * Doc shape:
 * ```
 * {
 *   userId, date: "YYYY-MM-DD", startedAt, updatedAt,
 *   routeIds: string[],   // array-contains index — the route-delete cascade needs it
 *   climbs: { [cid]: { routeId, at, kind, difficulty, discipline, holdColor, flash?, attempts? } },
 *           // kind "attempt" = a route project day, key `attempt_{routeId}` — never a climb
 *   totals: { climbs, sends, repeats, attempts, byDifficulty: { [key]: n } },  // ROUTE part only
 *   planId: null,         // reserved for the future training plan
 *   spray: { [entryId]: { problemId, kind, gradeIdx, at, ... } },   // Spraywall part
 *   sprayTotals: { sends, repeats, projects }, problemIds: string[],
 * }
 * ```
 *
 * One doc per climber-day holds both halves. Volume = route send + repeat plus
 * spray send + repeat (`dayVolume`); a spray project is activity, not a climb.
 *
 * `climbs` is a MAP (not an array) so a single entry can be removed with
 * `deleteField()` — `arrayRemove` would need the exact stored object, including
 * a server-written timestamp we don't have client-side.
 *
 * Everything here is pure so it can be unit-tested without Firebase. The core
 * the Cloud Functions also run (the kinds, the doc id, the map diff, the totals,
 * the route ids) lives once in `@sendly/shared` and is re-exported here; what
 * follows is the client-only half (entry building, cooldown, the stats views).
 */
import { CLIMB_KIND, climbAtMillis, computeClimbTotals, countAttemptEntries, isClimbEntry, logVolume, sprayLogTotals, sprayVolumeByBand, sprayDayLogEntries, volumeDates } from "@sendly/shared";

export { CLIMB_KIND, climbLogDocId, climbAtMillis, diffClimbMaps, computeClimbTotals, collectRouteIds } from "@sendly/shared";

/**
 * Guard against a double tap firing twice on the same route. This is NOT a rate
 * limit: repeats are worth zero points, so there is nothing to farm, and a
 * warm-up lap can legitimately come back to the same route within a minute.
 *
 * A quarter second is all a bounce needs: a ghost click or a finger
 * double-fire lands within ~100ms. It used to be 3s, and that blocked the
 * honest case — "again, and again" on a route you are lapping — where
 * reopening the modal and tapping the button takes one or two seconds.
 */
export const CLIMB_COOLDOWN_MS = 250;

/**
 * Build one climb entry from a route doc. The route's display fields are
 * denormalized onto the entry for the same reason `ascents` does it: once the
 * route is archived or hard-deleted, the historical volume must stay readable
 * without a join.
 */
export function buildClimbEntry(route, kind, { at, flash = false } = {}) {
  const entry = {
    routeId: route && route.id ? route.id : null,
    at: at ?? null,
    kind,
    difficulty: (route && route.difficulty) ?? null,
    discipline: (route && route.discipline) || "boulder",
    holdColor: (route && route.holdColor) ?? null,
  };
  // Only a genuine first send can be a flash. Stored here so the challenge
  // scorer never has to fetch the ascent doc just to know about the bonus.
  if (flash && kind === CLIMB_KIND.SEND) entry.flash = true;
  return entry;
}

/** Every entry in the map, oldest first. */
export function sortedClimbs(climbs) {
  return Object.entries(climbs || {})
    .map(([cid, entry]) => ({ cid, ...entry }))
    .sort((a, b) => climbAtMillis(a) - climbAtMillis(b));
}

/**
 * Climb entries (send / repeat) for one route, oldest first. A project-day
 * `attempt` entry is left out on purpose: the callers are the double-tap guard
 * (a "Próbáltam" must not block an immediate "Send!") and the quick log's "−",
 * which must never take back a try.
 */
export function climbsForRoute(climbs, routeId) {
  return sortedClimbs(climbs).filter(c => c.routeId === routeId && isClimbEntry(c));
}

/** How many times this route was climbed on this day. */
export function countClimbsForRoute(climbs, routeId) {
  return climbsForRoute(climbs, routeId).length;
}

/** The most recent entry for a route — what a "−1" affordance removes. */
export function lastClimbCidForRoute(climbs, routeId) {
  const list = climbsForRoute(climbs, routeId);
  return list.length ? list[list.length - 1].cid : null;
}

/**
 * True when this route was logged so recently that the tap is almost certainly
 * a double fire. The caller should SAY SO rather than silently swallowing the
 * tap — a silent no-op reads as "it didn't register" and invites more tapping.
 */
export function isWithinCooldown(climbs, routeId, nowMs, windowMs = CLIMB_COOLDOWN_MS) {
  const list = climbsForRoute(climbs, routeId);
  if (!list.length) return false;
  const last = climbAtMillis(list[list.length - 1]);
  if (!last) return false;
  return nowMs - last < windowMs;
}

/**
 * The route half of the day `date` from the live single-doc listener, or `{}`
 * when the listener still holds another day. Just after the gym's midnight it
 * is yesterday's document: read as today's, its tries would refuse a flash and
 * its entries would land in the new day's `totals`.
 */
export function liveDayClimbs(log, date) {
  return log && log.date === date ? (log.climbs || {}) : {};
}

/**
 * The identity and snapshot fields of a route's `attempt` entry. A second tap
 * the same day merges `{ attempts: increment(n), lastAt }` into the entry; it
 * carries these too, so if another device deleted the entry meanwhile the merge
 * recreates a whole entry rather than one without `routeId` / `kind`. The
 * snapshot of an existing entry wins over the route's current fields (a regrade
 * must not rewrite the day). `at` stays a create-only field.
 */
export function attemptEntryIdentity(route, existing = null) {
  const base = buildClimbEntry(route, CLIMB_KIND.ATTEMPT);
  const pick = (key) => (existing && existing[key] !== undefined ? existing[key] : base[key]);
  return {
    routeId: base.routeId,
    kind: CLIMB_KIND.ATTEMPT,
    difficulty: pick("difficulty"),
    discipline: pick("discipline"),
    holdColor: pick("holdColor"),
  };
}

/**
 * What removing ONE entry does to the route half of its day: the `totals` of
 * the map without it, and the route to `arrayRemove` from `routeIds` when no
 * entry of that route is left that day (null otherwise, and when the entry is
 * not in the map at all).
 */
export function planClimbRemoval(climbs, cid) {
  const next = { ...(climbs || {}) };
  const removed = next[cid] || null;
  delete next[cid];
  const routeId = removed && removed.routeId;
  const routeLeft = !!routeId && Object.values(next).some(e => e && e.routeId === routeId);
  return { totals: computeClimbTotals(next), dropRouteId: routeId && !routeLeft ? routeId : null };
}

/**
 * Flatten a set of log docs into a single event list, newest first, EVERY kind
 * included — the route modal's log area needs the project-day `attempt`
 * entries too. A caller that counts climbs filters with `isClimbEntry`.
 */
export function flattenClimbLogs(logs) {
  const out = [];
  for (const log of logs || []) {
    if (!log) continue;
    for (const [cid, entry] of Object.entries(log.climbs || {})) {
      if (!entry) continue;
      out.push({ ...entry, cid, userId: log.userId, date: log.date });
    }
  }
  return out.sort((a, b) => climbAtMillis(b) - climbAtMillis(a));
}

/**
 * Flatten log docs into ascent-SHAPED events.
 *
 * The admin charts were written against `ascents` docs (`routeDifficulty`,
 * `routeDiscipline`, `userId`, `date`). Handing them this shape lets them count
 * every climb instead of only first sends without rewriting their internals.
 *
 * `kind` rides along, and consumers MUST respect it wherever points are summed:
 * a repeat is worth zero, so a naive points reducer over this array would
 * inflate the gym's score.
 *
 * Project-day `attempt` entries are dropped HERE, once: every consumer counts
 * the rows as climbs (`kind !== "repeat"` reads as a send), so a try would
 * otherwise show up as a send worth points.
 */
export function climbLogsToAscentShape(logs) {
  return flattenClimbLogs(logs).filter(isClimbEntry).map(c => ({
    id: c.cid,
    userId: c.userId,
    routeId: c.routeId,
    date: c.date,
    routeDifficulty: c.difficulty ?? null,
    routeDiscipline: c.discipline || "boulder",
    routeHoldColor: c.holdColor ?? null,
    kind: c.kind,
    at: c.at,
    ...(c.flash ? { flash: true } : {}),
  }));
}

/**
 * Spray rows of the daily logs in the same ascent SHAPE the admin charts read
 * (`climbLogsToAscentShape`), one per spray `send` / `repeat` entry. A `project`
 * entry is not a climb and gives no row.
 *
 * Kept apart from `climbLogsToAscentShape` on purpose: that one stays the
 * route event stream (the challenge scorer and the route readers rely on it),
 * and a caller that wants spray says so by appending these rows. A spray row
 * carries no route difficulty (`routeDifficulty: null`), so a points reducer
 * over the merged array prices it at zero by construction; its grade rides
 * along as `gradeIdx` for the readers that sort it into a colour band.
 * `source: "spray"` tells the two kinds apart.
 */
export function sprayLogsToAscentShape(logs) {
  const out = [];
  for (const log of logs || []) {
    if (!log) continue;
    for (const e of sprayDayLogEntries([log])) {
      if (e.kind !== "send" && e.kind !== "repeat") continue;
      out.push({
        id: `${log.id || `${log.userId}_${log.date}`}/${e.entryId}`,
        source: "spray",
        userId: log.userId,
        routeId: null,
        problemId: e.problemId,
        date: e.date,
        routeDifficulty: null,
        routeDiscipline: "boulder",
        gradeIdx: e.gradeIdx,
        kind: e.kind,
        at: e.at,
        ...(e.flash ? { flash: true } : {}),
      });
    }
  }
  return out.sort((a, b) => (b.at || 0) - (a.at || 0));
}

/** A log doc with its route `totals` filled in from `climbs` when the doc carries none. */
function withRouteTotals(log) {
  if (!log || typeof log !== "object") return log;
  return log.totals ? log : { ...log, totals: computeClimbTotals(log.climbs) };
}

/**
 * The volume of one daily log doc, route and spray parts apart (`logVolume` of
 * `@sendly/shared`), tolerating a doc without stored `totals` — the live today
 * doc and the older test seeds have only the `climbs` map.
 */
export function dayVolume(log, { discipline = null } = {}) {
  return logVolume(withRouteTotals(log), { discipline });
}

/** True when a log doc holds at least one valid Spraywall entry (any kind). */
export function hasSprayEntries(log) {
  return sprayDayLogEntries([log]).length > 0;
}

/**
 * The spray part of the volume per difficulty key: `sprayVolumeByBand` summed
 * over the days. `bands` are the gym's REAL difficulty-level bands (no fixed
 * fallback band — its key matches no route key), so the result shares the key
 * space of `totals.byDifficulty`. A grade in no band is left out here and still
 * counts in the period's climb total.
 */
export function sumSprayVolumeByBand(logs, bands) {
  const out = {};
  if (!Array.isArray(bands) || bands.length === 0) return out;
  for (const log of logs || []) {
    if (!log) continue;
    for (const [key, n] of Object.entries(sprayVolumeByBand(log, bands))) out[key] = (out[key] || 0) + n;
  }
  return out;
}

/**
 * Sum `totals.byDifficulty` across days — the shape every volume chart and (later)
 * training-plan progress bar reads. O(days), never O(climbs). With `bands` the
 * spray send + repeat entries join their colour band (`sumSprayVolumeByBand`),
 * so the result is the whole climbing volume per level.
 */
export function sumVolumeByDifficulty(logs, { bands = null } = {}) {
  const out = {};
  for (const log of logs || []) {
    const by = (log && log.totals && log.totals.byDifficulty) || {};
    for (const [key, n] of Object.entries(by)) out[key] = (out[key] || 0) + (Number(n) || 0);
  }
  for (const [key, n] of Object.entries(sumSprayVolumeByBand(logs, bands))) out[key] = (out[key] || 0) + n;
  return out;
}

/**
 * Overlay the live "today" log doc onto a fetched range.
 *
 * Range reads go through a 60s cache and only re-run when the period changes,
 * so a climb logged while the Stats tab is open never appeared. The single-doc
 * listener already holds today's truth — splicing it in makes both a first send
 * and a repeat show up immediately, with no extra read.
 *
 * A first send used to *look* live only because the sibling charts are driven
 * by the `ascents` listener; the log-driven ones were equally stale. A repeat
 * touches no ascent doc, which is why it looked completely dead.
 *
 * An emptied today-doc (last climb removed) drops out rather than resurrecting
 * the stale fetched copy. "Empty" means no route climb AND no Spraywall entry:
 * the doc is shared, so a spray-only day must stay or a session logged on the
 * Spraywall tab would vanish from the Activity panel until the next fetch.
 */
export function mergeTodayLog(logs, todayLog, range = null) {
  const list = (logs || []).filter(Boolean);
  const date = todayLog && todayLog.date;
  if (!date) return list;
  if (range && ((range.from && date < range.from) || (range.to && date > range.to))) return list;
  const rest = list.filter(l => l.date !== date);
  if (!Object.keys(todayLog.climbs || {}).length && !hasSprayEntries(todayLog)) return rest;
  const merged = { ...todayLog, totals: todayLog.totals || computeClimbTotals(todayLog.climbs) };
  return [...rest, merged].sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

/**
 * A log doc's climb / first-send counts with COMPETITION climbs left out.
 *
 * The admin traffic chart is the one surface that must not see them: a
 * competition day is two orders of magnitude above a normal evening, and the
 * chart is read as "what does a normal day here look like". Everywhere else a
 * competition top is an ordinary climb and counts.
 *
 * Kept in step with the same exclusion in `functions/adminStats.js`, which
 * computes the older days of that identical chart — if the two disagreed, the
 * live day and the day before it would be drawn on different rules.
 *
 * Counted from the entries rather than read off `totals`, because `totals`
 * deliberately counts competition climbs (they are real sends and repeats).
 *
 * Route part ONLY. The same day doc also carries the Spraywall `spray` map;
 * its sends and repeats come from `sprayDayLogOverlay` over the very same docs,
 * and the admin overlay adds the two. Folding spray in here as well would
 * count every spray climb twice. A spray-only doc therefore gives zeros here —
 * a caller must not read that as "nothing happened" and drop the doc.
 *
 * A project-day `attempt` entry is not a climb either: it adds to neither
 * count, only to `attempts` (the number of routes tried) — the admin's live
 * active set takes a tries-only climber, the traffic numbers do not. Same rule
 * as the nightly builder.
 */
export function nonCompTotals(log) {
  let climbs = 0;
  let sends = 0;
  let attempts = 0;
  for (const entry of Object.values((log && log.climbs) || {})) {
    if (!entry || entry.compId) continue;
    if (entry.kind === CLIMB_KIND.ATTEMPT) { attempts += 1; continue; }
    if (!isClimbEntry(entry)) continue;
    climbs += 1;
    if (entry.kind === CLIMB_KIND.SEND) sends += 1;
  }
  return { climbs, sends, attempts };
}

/**
 * Per-day climb counts for the heatmap and the streak.
 *
 * Source is the `climbDates` map on `users/{uid}/gymStats/{gymId}`, which counts
 * EVERY climb — first sends and repeats — and needs no range query. Two Cloud
 * Function triggers share ownership of it, so it trails a just-logged climb by
 * a second or two; `todayLog` (the live listener) therefore WINS for today,
 * including when it drops to zero because the last climb was removed.
 *
 * Deliberately not discipline-filterable: `climbDates` carries no discipline
 * breakdown, and the heatmap has never been discipline-scoped either.
 */
export function climbCountsByDate(climbDates, todayLog = null) {
  const out = {};
  for (const [date, n] of Object.entries(climbDates || {})) {
    const c = Number(n) || 0;
    if (c > 0) out[date] = c;
  }
  const today = todayLog && todayLog.date;
  if (today) {
    const t = todayLog.totals || computeClimbTotals(todayLog.climbs);
    const c = Number(t.climbs) || 0;
    if (c > 0) out[today] = c;
    else delete out[today];
  }
  return out;
}

/**
 * Per-day route-try counts for the heatmap and the streak — the ACTIVITY pair of
 * `climbCountsByDate`, for a route project day (a route tried, not sent).
 *
 * Source is the `attemptDates` map on `users/{uid}/gymStats/{gymId}` (date →
 * number of routes only tried that day), written by the `attempt` branch of
 * `updateClimbLogStats` a second or two after the tap. `todayLog` (the live
 * listener) therefore WINS for today, zero included: without it a tries-only
 * today would vanish from the grid, because `climbCountsByDate` drops a day
 * whose live `totals.climbs` is 0.
 *
 * Activity only — a try is no climb, so this map never joins a volume reader.
 */
export function attemptCountsByDate(attemptDates, todayLog = null) {
  const out = {};
  for (const [date, n] of Object.entries(attemptDates || {})) {
    const c = Number(n) || 0;
    if (c > 0) out[date] = c;
  }
  const today = todayLog && todayLog.date;
  if (today) {
    const c = countAttemptEntries(todayLog.climbs);
    if (c > 0) out[today] = c;
    else delete out[today];
  }
  return out;
}

/**
 * The Spraywall part of the activity heatmap, split for its tooltip: per day the
 * number of spray ENTRIES (the cell's unit, unchanged) and how many of them are
 * spray PROJECT entries (a try without a send, not spray climbing).
 *
 * The `gymStats` document stores no project count, but it stores two maps that
 * give it: `sprayDates[day]` counts every object in the day's `spray` map, and
 * `sprayDayStats[day]` counts the valid sends (`s`) and the valid repeats (`r`,
 * one entry per repeat). A project is the third kind, so
 * `projects = sprayDates[day] - s - r` (never below 0). A day whose stats key is
 * missing, or an older one without the integer `r` (before the backfill the
 * subtraction would count the repeats as projects), is NOT split: all its
 * entries stay "spray" and it contributes no project.
 *
 * Today's live log wins for BOTH figures when it is passed (zero included), the
 * same way `attemptCountsByDate` treats the route tries: the entries and the
 * projects are counted from the live doc's own `spray` map, so the tooltip is
 * right during the trigger's few-seconds lag.
 *
 * @returns {{ entries: Object<string, number>, projects: Object<string, number> }}
 *   `entries` is the sanitised `sprayDates` (positive counts only, with today
 *   replaced by the live count); `projects` holds only days with at least one.
 */
export function spraySplitByDate(sprayDates, sprayDayStats = null, todayLog = null) {
  const entries = {};
  const projects = {};
  const isCount = (v) => Number.isFinite(v) && v >= 0;
  for (const [date, raw] of Object.entries(sprayDates || {})) {
    const n = Number(raw);
    if (!(n > 0)) continue;
    entries[date] = n;
    const stats = sprayDayStats && typeof sprayDayStats === "object" ? sprayDayStats[date] : null;
    if (!stats || !isCount(stats.s) || !isCount(stats.r)) continue;
    const p = n - stats.s - stats.r;
    if (p > 0) projects[date] = p;
  }
  const today = todayLog && todayLog.date;
  if (today) {
    const live = sprayLogTotals(todayLog);
    const n = live.sends + live.repeats + live.projects;
    if (n > 0) {
      entries[today] = n;
      if (live.projects > 0) projects[today] = live.projects;
      else delete projects[today];
    } else {
      delete entries[today];
      delete projects[today];
    }
  }
  return { entries, projects };
}

/**
 * Per-day VOLUME — route climbs plus spray sends and repeats — for the volume
 * readers (the progression chart's "Mászás" view). Returns the total map and
 * its spray part, so a chart can draw the spray share as its own fainter fill.
 *
 * Today's live doc wins for BOTH halves while the triggers lag: the route half
 * through `climbCountsByDate`, the spray half from the doc's own `spray` map
 * (`sprayLogTotals`). The activity readers (heatmap, streak) keep calling
 * `climbCountsByDate` with `sprayDates`; this volume map is never added to that
 * activity union, or the same spray entries would be counted twice.
 *
 * @returns {{ total: Object<string, number>, spray: Object<string, number> }}
 */
export function volumeCountsByDate({ climbDates = null, sprayDayStats = null, todayLog = null } = {}) {
  const routeDates = climbCountsByDate(climbDates, todayLog);
  const sprayStats = { ...(sprayDayStats && typeof sprayDayStats === "object" ? sprayDayStats : {}) };
  const today = todayLog && todayLog.date;
  if (today) {
    const live = sprayLogTotals(todayLog);
    if (live.climbs > 0) sprayStats[today] = { s: live.sends, r: live.repeats };
    else delete sprayStats[today];
  }
  return {
    total: volumeDates({ climbDates: routeDates, sprayDayStats: sprayStats }),
    spray: volumeDates({ sprayDayStats: sprayStats }),
  };
}

/**
 * Total climbs inside an inclusive "YYYY-MM-DD" range, from a date→count map.
 *
 * `from`/`to` may be null for an open end, matching `resolvePeriod`'s shape, so
 * an "all time" period needs no special case. String comparison is exact for
 * zero-padded ISO dates and sidesteps every timezone question a Date round-trip
 * would reintroduce.
 *
 * This is how a friend's climb count is obtained without reading their log: one
 * public `users/{uid}/gymStats/{gymId}` doc carries the whole map, versus a
 * date-ranged query per climber per period change.
 */
export function sumClimbsInRange(climbDates, from = null, to = null) {
  let total = 0;
  for (const [date, n] of Object.entries(climbDates || {})) {
    if (from && date < from) continue;
    if (to && date > to) continue;
    total += Number(n) || 0;
  }
  return total;
}

/**
 * Roll a date→count map up into weekStart→count, for the progression chart's
 * "climbs" mode.
 *
 * `weekStartOf` is injected rather than imported so this module stays free of
 * `src/utils.js`.
 */
export function climbsByWeek(countsByDate, weekStartOf) {
  const out = {};
  for (const [date, n] of Object.entries(countsByDate || {})) {
    const c = Number(n) || 0;
    if (c <= 0) continue;
    const ws = weekStartOf(date);
    if (!ws) continue;
    out[ws] = (out[ws] || 0) + c;
  }
  return out;
}

/** One decimal, but drop a trailing ".0" — "24" reads better than "24.0".
 *  Used for every "Ø climbs per session / per day" figure. */
export function formatClimbAvg(n) {
  if (!n) return "0";
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/**
 * Period-level roll-up for the Activity panel.
 *
 * Volume is route climbs plus spray sends and repeats (`dayVolume`); a spray
 * project is no climb. A workout is one day doc with at least one climb, so a
 * spray-only day with a send is a workout, while a day of spray projects only
 * (or nothing) is not. `newRoutes` is the route first sends alone — a spray
 * send is never a "new route" — while `sends` / `repeats` count both kinds and
 * `sprayClimbs` is the spray share of `climbs`. A caller that must drop the
 * spray part (the lead filter) removes the docs' `spray` map before calling.
 */
export function summarizeClimbLogs(logs) {
  const list = (logs || []).filter(Boolean);
  let climbs = 0, sends = 0, repeats = 0, newRoutes = 0, sprayClimbs = 0, longest = 0, sessions = 0;
  for (const log of list) {
    const v = dayVolume(log);
    climbs += v.climbs;
    sends += v.sends;
    repeats += v.repeats;
    newRoutes += v.route.sends;
    sprayClimbs += v.spray.climbs;
    longest = Math.max(longest, v.climbs);
    if (v.climbs > 0) sessions += 1;
  }
  return {
    sessions,
    climbs,
    sends,
    repeats,
    newRoutes,
    sprayClimbs,
    longestSession: longest,
    avgPerSession: sessions > 0 ? climbs / sessions : 0,
  };
}

/**
 * Live overlay numbers from the Spraywall half of the daily logs (D94), for the
 * admin stats page.
 *
 * Reads the `spray` map of the SAME `climbLogs` docs the route overlay reads
 * (one listener carries both halves), so the admin page adds these counts to
 * the route counts of `nonCompTotals` without counting anything twice: that one
 * reads `climbs` only, this one `spray` only. A spray-only doc still yields its
 * entries here even though its route part is empty.
 *
 * Same rule as the nightly builder (`apps/functions/shared/sprayPresence.js`,
 * `functions/adminStats.js`): a `project` attempt makes the climber ACTIVE but
 * is not a climb, so it counts for presence (`todayUids`) and for neither day
 * count. `send` is a first send (both counts); `repeat` is a climb that is not
 * a first send (`dayCounts` only). A competition entry (`compId`, from a spray
 * competition's close sync) counts for nothing here, like the route side's
 * `nonCompTotals`.
 *
 * Deliberately a different name and body from the server helper: the two
 * runtimes share a rule, not a function (`noNewTwins`).
 *
 * @param {Array<object>|null} logs climbLogs documents (route-only docs give nothing)
 * @param {string|null} todayKey gym-timezone date key
 * @returns {{ todayUids: string[], dayCounts: Object<string, number>, dayNewCounts: Object<string, number> }}
 */
export function sprayDayLogOverlay(logs, todayKey) {
  const today = new Set();
  const dayCounts = {};
  const dayNewCounts = {};
  for (const log of Array.isArray(logs) ? logs : []) {
    if (!log || !log.date) continue;
    for (const entry of Object.values(log.spray || {})) {
      if (!entry || typeof entry !== "object" || entry.compId) continue;
      if (entry.kind === "send") {
        dayCounts[log.date] = (dayCounts[log.date] || 0) + 1;
        dayNewCounts[log.date] = (dayNewCounts[log.date] || 0) + 1;
      } else if (entry.kind === "repeat") {
        dayCounts[log.date] = (dayCounts[log.date] || 0) + 1;
      } else if (entry.kind !== "project") {
        continue;
      }
      if (log.date === todayKey && log.userId) today.add(log.userId);
    }
  }
  return { todayUids: [...today], dayCounts, dayNewCounts };
}
